import React, { useState, useEffect, ReactNode } from 'react'
import { Typography, CircularProgress, Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { FormattedMessage } from 'react-intl'
import { Field, useFormikContext } from 'formik'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import CheckMarkIcon from '@icons/CheckMarkIcon'
import ErrorIcon from '@material-ui/icons/Error'
import {
  EventMethods,
  sendShareDialogEvent,
  ShareItemDetailsModalEventAction,
} from '@src/routes/OrderCenter/analytics'
import {
  ShareDialogItemTypes,
  ShareDialogFormValues,
  ShareDialogOrderDetailsItemProps,
} from '@src/components/ShareDialog/ShareDialog'
import { getLocalizedUrl } from '@utils/regional'
import { useUserSession } from '@utils/useUserSession'
import { useShareDialog } from '../useShareDialog'
import { orderCenterRoutes } from '@src/routes'

interface ShareDialogDirectLinkProps {
  itemNumber: string
  itemType: ShareDialogItemTypes
  directLinkDescription: ReactNode
  order?: ShareDialogOrderDetailsItemProps
}

const useStyles = makeStyles((theme) => ({
  directLinkLayout: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  subLabel: {
    margin: theme.spacing(1, 0),
    display: 'block',
  },
  readOnly: {
    backgroundColor: '#f9f9fd', // one-off color not similar enough to palette colors
  },
  shareLinkContainer: {
    position: 'relative',
    width: '100%',
  },
  shareLink: {
    width: '100%',
  },
  loadingSpinner: {
    transform: 'translate(-50%, -50%)',
    position: 'absolute',
    left: '50%',
    top: '50%',
    display: 'flex',
    alignItems: 'center',
  },
  errorMessage: {
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    fontSize: theme.typography.pxToRem(16),
  },
}))

const ShareDialogDirectLink: React.FC<ShareDialogDirectLinkProps> = ({
  itemType,
  itemNumber,
  directLinkDescription,
  order,
}) => {
  const classes = useStyles()
  const [isCopied, setIsCopied] = useState(false)
  const { userSession } = useUserSession()
  const { values, setFieldValue } = useFormikContext<ShareDialogFormValues>()
  const [hasError, setHasError] = useState(false)

  const { linkQuery, linkPropertyName, linkData, linkError } = useShareDialog(
    itemType,
    itemNumber
  )

  // append tracking params to current product page URL
  const getProductShareLink = () => {
    const urlWithNewParams = new URL(window.location.href)
    urlWithNewParams.searchParams.append(
      'icid',
      'sharepdp-clipboard-copy-productdetailpage'
    )
    setFieldValue('shareLink', urlWithNewParams)
  }

  // append the domain onto the share URL (if provided from BE)
  const getAbsoluteUrl = (itemShareableLink: string) => {
    return `${window.location.origin}${
      getLocalizedUrl(userSession, itemShareableLink).as
    }`
  }

  // append params to current order page URL
  const getOrderShareLink = () => {
    const soldTo = order?.soldTo ?? ''
    const postalCode = order?.postalCode ?? ''
    const shareURL = `${orderCenterRoutes.orderDetail(
      itemNumber || ''
    )}?postalCode=${postalCode}&soldTo=${soldTo}`
    setFieldValue('shareLink', getAbsoluteUrl(shareURL))
    setFieldValue('relativeShareLink', shareURL)
  }

  // Get a share URL
  useEffect(() => {
    if (!values.shareLink) {
      if (linkQuery) {
        linkQuery()
      } else if (itemType === ShareDialogItemTypes.Order) {
        getOrderShareLink()
      } else {
        getProductShareLink()
      }
    }
  }, [itemNumber])

  // if order or quote comes back with data, then get the link
  useEffect(() => {
    if (linkData && linkPropertyName) {
      setHasError(false)
      setFieldValue('shareLink', getAbsoluteUrl(linkData[linkPropertyName]))
      setFieldValue('relativeShareLink', linkData[linkPropertyName])
    }
  }, [linkData])

  // if order or quote comes back with an error, then set error
  useEffect(() => {
    if (linkError) {
      setHasError(true)
    }
  }, [linkError])

  // copy the URL
  useEffect(() => {
    if (isCopied) {
      const isCopiedTimeout = setTimeout(() => setIsCopied(false), 3000)
      return () => clearTimeout(isCopiedTimeout)
    }
    return undefined
  }, [isCopied])

  const handleCopyClick = (value: string) => {
    if (!isCopied) {
      sendShareDialogEvent({
        action: ShareItemDetailsModalEventAction.Copy,
        itemNumber: itemNumber,
        contentType: itemType,
        method: EventMethods.CopyLink,
        component: 'modal',
        elementType: 'button',
        elementText: 'copy',
      })
    }
    navigator.clipboard.writeText(value).then(() => setIsCopied(true))
  }

  return (
    <>
      <Typography variant="h3" component="label" htmlFor="share_url">
        <FormattedMessage id="DIRECT_LINK" defaultMessage="Direct Link" />
      </Typography>
      <p id="direct_link_description" className={classes.subLabel}>
        {directLinkDescription}
      </p>
      <div className={classes.directLinkLayout}>
        <div className={classes.shareLinkContainer}>
          <Field
            id="share_url"
            className={`${classes.readOnly} ${classes.shareLink}`}
            data-testid={`${itemType}-details-share-dialog-url`}
            readOnly
            aria-describedby="direct_link_description"
            name="shareLink"
            component={LiquidInputAdapter}
          />
          {!values.shareLink && !hasError && (
            <div className={classes.loadingSpinner}>
              <CircularProgress size={20} />
            </div>
          )}
        </div>
        <Button
          color="primary"
          data-testid={`${itemType}-details-share-dialog-copy-btn`}
          id={`${itemType}-details-share-dialog-copy-btn`}
          disabled={!values.shareLink}
          onClick={() => handleCopyClick(values.shareLink || 'fixme')}
          type="button"
          variant="outlined"
        >
          {!isCopied && <FormattedMessage id="COPY" defaultMessage="Copy" />}
          {isCopied && (
            <>
              <CheckMarkIcon style={{ fontSize: 16, marginRight: 6 }} />
              <FormattedMessage id="COPIED" defaultMessage="Copied" />
            </>
          )}
        </Button>
      </div>
      {hasError && (
        <div className={classes.errorMessage}>
          <ErrorIcon className={classes.errorIcon} color="error" />
          <FormattedMessage
            id="LINK_REQUEST_FAILED"
            defaultMessage="Request for link failed"
          />
        </div>
      )}
    </>
  )
}
export default ShareDialogDirectLink
